import axios, { AxiosResponse } from "axios";
import Cookies from "js-cookie";
import { getApiBaseUrl } from "../utils/apiConfig";
import { sessionManager } from "../utils/sessionManager";

// HTTP method constants
export const HttpMethod = Object.freeze({
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  DELETE: "DELETE",
  PATCH: "PATCH",
} as const);

export type HttpMethodType = (typeof HttpMethod)[keyof typeof HttpMethod];

const DEV_URL = "https://dev.lab.nephrolytics.ai";

// Base axios options interface
export interface BaseAxiosOptions {
  baseURL: string;
  url: string;
  headers: {
    "X-CSRFToken": string | undefined;
  };
}

// Query filter interface
export interface QueryFilter {
  field: string;
  operator: string;
  value: string | number | boolean;
}

// Query sort interface
export interface QuerySort {
  field: string;
  order: "asc" | "desc";
}

// Query options interface
export interface QueryOptions {
  filter?: QueryFilter;
  sort?: QuerySort;
  page?: string | number;
}

// Generic API response type
export type ApiResponse<T = any> = AxiosResponse<T>;

export async function getAxiosBaseOptions(method: HttpMethodType, url: string): Promise<BaseAxiosOptions> {
  const baseURL = getApiBaseUrl();
  let formattedUrl = url;

  // In development, remove DEV_URL from URL to work with proxy
  if (!(import.meta as any).env?.MODE || (import.meta as any).env?.MODE === "development") {
    formattedUrl = removeDevUrlFromUrl(url);
  }

  if (!Cookies.get("csrftoken") && method !== HttpMethod.GET) {
    await axios.request({
      baseURL,
      method: HttpMethod.GET,
      url: "/api/csrf-token/",
    });
  }

  return {
    baseURL,
    url: formattedUrl,
    headers: {
      "X-CSRFToken": Cookies.get("csrftoken"),
    },
  };
}

// Parse query options into URL search params
// Example: {filter: {field: "name", operator: "contains", value: "John"}, sort: {field: "name", order: "asc"}}
export function parseQueryOptions(queryOptions: QueryOptions): string {
  const params = new URLSearchParams();

  if (queryOptions.filter) {
    params.append("filter", JSON.stringify(queryOptions.filter));
  }

  if (queryOptions.sort) {
    params.append("sort", JSON.stringify(queryOptions.sort));
  }

  if (queryOptions.page) {
    params.append("page", queryOptions.page.toString());
  }

  return params.toString();
}

export async function apiGet<T = any>(url: string, signal?: AbortSignal): Promise<ApiResponse<T>> {
  const method = HttpMethod.GET;
  const options = await getAxiosBaseOptions(method, url);

  try {
    return await axios.request<T>({
      ...options,
      method,
      signal,
    });
  } catch (error) {
    if (sessionManager.isSessionExpired(error)) {
      sessionManager.handleSessionExpiry("React Query GET");
    }
    throw error;
  }
}

export async function apiPost<TData = any, TResponse = any>(url: string, data?: TData): Promise<ApiResponse<TResponse>> {
  const method = HttpMethod.POST;
  const options = await getAxiosBaseOptions(method, url);

  try {
    return await axios.request<TResponse>({
      ...options,
      method,
      data,
    });
  } catch (error) {
    if (sessionManager.isSessionExpired(error)) {
      sessionManager.handleSessionExpiry("React Query POST");
    }
    throw error;
  }
}

export async function apiPut<TData = any, TResponse = any>(url: string, data?: TData): Promise<ApiResponse<TResponse>> {
  const method = HttpMethod.PUT;
  const options = await getAxiosBaseOptions(method, url);

  try {
    return await axios.request<TResponse>({
      ...options,
      method,
      data,
    });
  } catch (error) {
    if (sessionManager.isSessionExpired(error)) {
      sessionManager.handleSessionExpiry("React Query PUT");
    }
    throw error;
  }
}

export async function apiDelete<TData = any, TResponse = any>(url: string, data?: TData): Promise<ApiResponse<TResponse>> {
  const method = HttpMethod.DELETE;
  const options = await getAxiosBaseOptions(method, url);

  try {
    return await axios.request<TResponse>({
      ...options,
      method,
      data,
    });
  } catch (error) {
    if (sessionManager.isSessionExpired(error)) {
      sessionManager.handleSessionExpiry("React Query DELETE");
    }
    throw error;
  }
}

export async function apiPatch<TData = any, TResponse = any>(url: string, data?: TData): Promise<ApiResponse<TResponse>> {
  const method = HttpMethod.PATCH;
  const options = await getAxiosBaseOptions(method, url);

  try {
    return await axios.request<TResponse>({
      ...options,
      method,
      data,
    });
  } catch (error) {
    if (sessionManager.isSessionExpired(error)) {
      sessionManager.handleSessionExpiry("React Query PATCH");
    }
    throw error;
  }
}

// During development, the API URL is prefixed with the DEV_URL constant.
// To allow the proxy server to forward requests to the API server, the DEV_URL is removed from the URL.
function removeDevUrlFromUrl(url: string): string {
  return url.replace(DEV_URL, "");
}
